/**
 * How a job gets billed — §6.9's "billing settings on a job".
 *
 * Five kinds of schedule, and the difference between them is the difference
 * between a landscaper's contracts: mow every week and bill what you did (per
 * visit), take the same amount every month whatever the weather (monthly
 * flat), bill a patio as it is built (milestones or progress), or send one
 * invoice when the work is finished (on completion).
 *
 * The preview underneath is the point of the screen. It runs the same
 * `linesFor` the 06:00 run does, so what it shows is what will actually be
 * sent — a preview built from its own query is a preview of something else,
 * and the first time they disagree is in front of a customer.
 */
import { useCallback, useEffect, useState } from 'react'
import { Button, Card, EmptyState, SectionLabel, StatusChip, Switch, Tag, humanize } from './primitives.js'
import { Field, SelectField } from './fields.js'
import { DateField } from './date-field.js'
import { EM_DASH, formatDollars, formatListDate } from '../lib/format.js'
import { request } from '../lib/api.js'

const KINDS = [
  { value: 'on_completion', label: 'When the work is done',
    hint: 'One invoice, once the last visit is closed out.' },
  { value: 'per_visit', label: 'Per visit',
    hint: 'Everything closed out since the last invoice, on the day of the month you pick.' },
  { value: 'monthly_flat', label: 'Monthly flat',
    hint: 'The same amount every month, whatever the weather did.' },
  { value: 'milestones', label: 'Milestones',
    hint: 'From the approved quote’s schedule — deposit, start, completion.' },
  { value: 'progress', label: 'Progress',
    hint: 'A share of the contract as the work is done.' },
] as const

interface Schedule {
  kind: string
  autoSend: boolean
  autopay: boolean
  skipEmpty: boolean
  termsDays: number
  dayOfMonth: number | null
  billAhead: boolean
  flatAmount: string | null
  nextBillOn: string | null
  seasonStart: string | null
  seasonEnd: string | null
  lateFeeAllowed: boolean
  attachCloseout: boolean
  billToContactId: string | null
}

interface Preview {
  period: { start: string; end: string }
  lines: { description: string; amountCents: number }[]
  total: string
}

interface Payload {
  schedule: Schedule | null
  milestones: { id: string; label: string; dueOn: string | null; amount: string
                status: string }[]
  preview: Preview | null
  upcoming: { id: string; reference: string; status: string; issueDate: string
              dueDate: string; total: string }[]
}

const EMPTY: Schedule = {
  kind: 'on_completion', autoSend: false, autopay: false, skipEmpty: true, termsDays: 30,
  dayOfMonth: 1, billAhead: false, flatAmount: null, nextBillOn: null,
  seasonStart: null, seasonEnd: null, lateFeeAllowed: false, attachCloseout: false,
  billToContactId: null,
}

export function JobBilling({ jobId, canEdit }: { jobId: string; canEdit: boolean }) {
  const [data, setData] = useState<Payload | null>(null)
  const [form, setForm] = useState<Schedule>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const payload = await request<Payload>(`/billing/${jobId}`)
      setData(payload)
      setForm(payload.schedule ?? EMPTY)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be loaded.')
    }
  }, [jobId])

  useEffect(() => { void load() }, [load])

  const set = <K extends keyof Schedule>(key: K, value: Schedule[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await request(`/billing/${jobId}`, { method: 'PUT', body: form })
      setNotice('Saved. The preview below is what the next run will send.')
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (!data) {
    return <p className="p-4 text-cell text-text-muted">{error ?? 'Loading…'}</p>
  }

  const monthly = form.kind === 'monthly_flat'
  const dated = form.kind === 'per_visit' || monthly || form.kind === 'progress'

  return (
    <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card className="p-3">
        <SectionLabel className="mb-2 block">How this job is billed</SectionLabel>

        {error && (
          <p className="m-0 mb-2 rounded-card border border-danger bg-danger-bg p-2 text-cell text-danger">
            {error}
          </p>
        )}
        {notice && <p className="m-0 mb-2 text-sub text-accent-dark">{notice}</p>}

        <div className="flex flex-col gap-2.5">
          <SelectField
            label="Schedule"
            value={form.kind}
            onChange={(value) => set('kind', value)}
            hint={KINDS.find((entry) => entry.value === form.kind)?.hint}
          >
            {KINDS.map((entry) => (
              <option key={entry.value} value={entry.value}>{entry.label}</option>
            ))}
          </SelectField>

          {monthly && (
            <Field
              label="Amount each month"
              value={form.flatAmount ?? ''}
              onChange={(value) => set('flatAmount', value || null)}
              required
              hint="A flat contract with no amount would bill nothing, so this is refused empty."
            />
          )}

          {dated && (
            <div className="grid grid-cols-2 gap-2">
              <Field
                label="Day of the month"
                value={String(form.dayOfMonth ?? '')}
                onChange={(value) => set('dayOfMonth', value ? Number(value) : null)}
                hint="1–28, so February is never a surprise."
              />
              <Field
                label="Payment terms (days)"
                value={String(form.termsDays)}
                onChange={(value) => set('termsDays', Number(value) || 0)}
              />
            </div>
          )}

          {!dated && (
            <Field
              label="Payment terms (days)"
              value={String(form.termsDays)}
              onChange={(value) => set('termsDays', Number(value) || 0)}
            />
          )}

          <div className="grid grid-cols-2 gap-2">
            {/* `DateField` speaks in empty strings; the schedule stores nulls,
                because "no season end" is an absence rather than a blank. */}
            <DateField
              size="field"
              label="Next bill on"
              value={form.nextBillOn ?? ''}
              onChange={(value) => set('nextBillOn', value || null)}
            />
            <DateField
              size="field"
              label="Season ends"
              value={form.seasonEnd ?? ''}
              onChange={(value) => set('seasonEnd', value || null)}
            />
          </div>

          <div className="flex flex-col gap-2 border-t border-line-soft pt-2.5">
            {/*
              * The switch §6.9's acceptance test is about: "turning auto-send
              * off on a schedule produces a draft next cycle instead of a sent
              * invoice". It is first because it is the one with a customer on
              * the other end of it.
              */}
            <Switch
              checked={form.autoSend}
              onChange={(value) => set('autoSend', value)}
              label="Send it automatically at 6am"
            />
            <p className="m-0 -mt-1 text-sub text-text-muted">
              {form.autoSend
                ? 'Created and sent on the schedule day, in your timezone.'
                : 'Created as a draft in Awaiting approval, for somebody to look at first.'}
            </p>
            <Switch
              checked={form.skipEmpty}
              onChange={(value) => set('skipEmpty', value)}
              label="Skip a month with nothing in it"
            />
            <Switch
              checked={form.lateFeeAllowed}
              onChange={(value) => set('lateFeeAllowed', value)}
              label="Charge a late fee on this job"
            />
            <Switch
              checked={form.attachCloseout}
              onChange={(value) => set('attachCloseout', value)}
              label="Attach the close-out photos"
            />
          </div>

          {canEdit && (
            <div className="flex justify-end">
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {busy ? 'Saving…' : 'Save billing'}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <div className="flex min-w-0 flex-col gap-3">
        <Card className="p-3">
          <SectionLabel className="mb-2 block">What the next run would send</SectionLabel>
          {data.preview && data.preview.lines.length > 0
            ? (
              <>
                <p className="m-0 mb-2 text-sub text-text-muted">
                  {formatListDate(data.preview.period.start)} to{' '}
                  {formatListDate(data.preview.period.end)}
                </p>
                <ul className="m-0 list-none p-0">
                  {data.preview.lines.map((line, index) => (
                    <li key={index} className="flex items-baseline justify-between gap-3
                      border-b border-line-soft py-1.5 last:border-b-0">
                      <span className="min-w-0 flex-1 truncate text-cell text-text">
                        {line.description}
                      </span>
                      <span className="tnum shrink-0 text-cell text-text">
                        {formatDollars(line.amountCents / 100)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
                  <span className="text-cell font-semibold text-text">Total</span>
                  <span className="tnum text-cell font-bold text-text">
                    {formatDollars(Number(data.preview.total))}
                  </span>
                </div>
              </>
            )
            : (
              <p className="m-0 py-4 text-center text-cell text-text-muted">
                Nothing to bill in this period.
                {form.skipEmpty && ' The run will skip it.'}
              </p>
            )}
        </Card>

        {data.milestones.length > 0 && (
          <Card className="p-3">
            <SectionLabel className="mb-2 block">Milestones</SectionLabel>
            <ul className="m-0 list-none p-0">
              {data.milestones.map((milestone) => (
                <li key={milestone.id} className="flex items-center justify-between gap-2
                  border-b border-line-soft py-1.5 last:border-b-0">
                  <span className="min-w-0 flex-1 truncate text-cell text-text">
                    {milestone.label}
                  </span>
                  <Tag>{humanize(milestone.status)}</Tag>
                  <span className="tnum shrink-0 text-cell text-text">
                    {formatDollars(Number(milestone.amount))}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="p-3">
          <SectionLabel className="mb-2 block">Invoices from this schedule</SectionLabel>
          {data.upcoming.length === 0
            ? (
              <EmptyState
                title="Nothing billed yet"
                body="Invoices from this job's schedule show up here as they are created."
              />
            )
            : (
              <ul className="m-0 list-none p-0">
                {data.upcoming.map((invoice) => (
                  <li key={invoice.id} className="flex items-center justify-between gap-2
                    border-b border-line-soft py-1.5 last:border-b-0">
                    <span className="shrink-0 text-cell font-semibold text-text">
                      {invoice.reference}
                    </span>
                    <StatusChip group="invoice" status={invoice.status}>
                      {humanize(invoice.status)}
                    </StatusChip>
                    <span className="min-w-0 flex-1 truncate text-right text-sub text-text-muted">
                      {invoice.dueDate ? `due ${formatListDate(invoice.dueDate)}` : EM_DASH}
                    </span>
                    <span className="tnum shrink-0 text-cell text-text">
                      {formatDollars(Number(invoice.total))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        </Card>
      </div>
    </div>
  )
}
